import React, { useRef, useState } from "react";
import imageCompression from "browser-image-compression";
import { toast } from "sonner";
import { imgUpload } from "../../asset";

interface FileUploadProps<T extends boolean = false> {
  value?: T extends true ? string[] : string;
  onUploadComplete: (urls: T extends true ? string[] : string) => void;
  label?: React.ReactNode;
  folder?: string;
  className?: string;

  placeholderImage?: string;
  borderStyle?: "border-dashed" | "solid";
  buttonText?: string;
  buttonWidth?: "full" | "fit";
  buttonAlign?: "left" | "center" | "right";
  disabled?: boolean;
  buttonColor?: string;
  showButton?: boolean;
  showDropzone?: boolean;
  text?: boolean;
  uploadText?: string;
  multiple?: boolean;
}

export default function FileUpload<T extends boolean = false>({
  label,
  onUploadComplete,
  folder = "school_mgmt",
  className = "aspect-square min-w-[3rem]",
  value,
  placeholderImage = imgUpload,
  borderStyle = "border-dashed",
  buttonText = "Choose Photo",
  buttonWidth = "full",
  buttonAlign = "center",
  disabled = false,
  buttonColor = "bg-primary-500",
  showButton = true,
  showDropzone = true,
  text = false,
  uploadText = "Change Image",
  multiple = false,
}: FileUploadProps<T>) {
  // const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    const files = e.target.files;
    if (!files || files.length === 0) return;

    const uploadedUrls: string[] = [];

    for (const file of Array.from(files)) {
      let fileToUpload = file;

      // compression (same logic)
      if (file.size > 4 * 1024 * 1024) {
        try {
          fileToUpload = await imageCompression(file, {
            maxSizeMB: 2.9,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
            fileType: file.type,
          });
        } catch {
          toast.error("Image compression failed");
          continue;
        }
      }

      const url = await uploadFile(fileToUpload);
      if (url) uploadedUrls.push(url);
    }

    if (uploadedUrls.length > 0) {
      if (multiple) {
        onUploadComplete(uploadedUrls as any);
      } else {
        onUploadComplete(uploadedUrls[0] as any);
      }
    }

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const uploadFile = async (file: File): Promise<string | null> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", folder);

    try {
      setIsUploading(true);

      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL_IMAGE}api/file-upload`,
        {
          method: "POST",
          body: formData,
        },
      );

      if (!res.ok) {
        toast.error("Upload failed");
        return null;
      }

      const data = await res.json();
      return data?.file ?? null;
    } catch (err) {
      toast.error("Upload error");
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const handleClick = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled) return;

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    await uploadFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const displayImage = value;
  // const displayImage = value || previewUrl;

  const alignClass =
    buttonAlign === "left"
      ? "justify-start"
      : buttonAlign === "right"
        ? "justify-end"
        : "justify-center";

  return (
    <div className={"relative group  " + className}>
      {label && (
        <span className="text-sm bg-white px-1 mb-2 text-neutral-400 font-semibold">
          {label}
        </span>
      )}

      {/* Hidden file input */}
      <input
        type="file"
        accept="image/*"
        ref={inputRef}
        multiple={multiple}
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Clickable upload area */}
      {showDropzone && (
        <div
          className={`w-full h-full rounded-xl py-1 cursor-pointer transition-all relative  overflow-hidden ${
            isUploading
              ? "border-primary-500 bg-blue-50"
              : value
                ? ""
                : `border-neutral-100 hover:border-primary-500 border-2 ${borderStyle}`
          }`}
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          {isUploading ? (
            <div className="flex flex-col items-center justify-center h-full text-primary-500">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mb-3"></div>
              <span className="text-sm font-medium">Uploading...</span>
            </div>
          ) : value ? (
            <>
              <div className="flex flex-col items-center justify-center h-full">
                <img
                  src={Array.isArray(value) ? value[0] : value}
                  alt="preview"
                  className="w-full h-full object-contain"
                />
              </div>
            </>
          ) : (
            <div className="flex flex-col gap-3 items-center justify-center h-full transition-colors">
              <img src={placeholderImage} alt="click to upload" />
              {text && (
                <p className="text-neutral-400 text-lg ">
                  Drag and drop files here, or click to upload.
                </p>
              )}
            </div>
          )}
        </div>
      )}
      {showButton && (
        <div className={`flex ${alignClass}`}>
          <button
            type="button"
            onClick={handleClick}
            disabled={disabled}
            className={`rounded-lg capitalize mt-2 text-center py-[9.5px] px-6 text-sm font-semibold text-white ${
              buttonWidth === "full" ? "w-full" : "w-fit"
            } ${disabled ? "bg-primary-200 cursor-not-allowed" : buttonColor}`}
          >
            {value ? uploadText : buttonText}
          </button>
        </div>
      )}
    </div>
  );
}
